import { useMemo } from "react";
import DataTable from "../../../shared/dashboards/DataTable/DataTable";
import {
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  CreditCardOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  RollbackOutlined,
} from "@ant-design/icons";

import styles from "./stylesInProgressTable.module.css";

import CustomPagination from "../../../shared/CustomPagination/CustomPagination";
import { useWithdrawalReview } from "../WithdrawalReviewContext";
import {
  convertToUserTimezone,
  formatTimestampOrDisplay,
} from "../../../../utils/dateService";
import UserAvatar from "../../../CommonComponents/UserAvatar/UserAvatar";
import { formatCurrency } from "../../../../utils/financialFormatters";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import {
  WalletBalanceCell,
  WalletColumnTitle,
} from "../../../shared/WalletBalanceCell";

function renderRequestDate(text) {
  return <span>{convertToUserTimezone(text)}</span>;
}

function renderUsernameCell(text, record, handleViewUser) {
  return (
    <div className={styles.userImage}>
      <UserAvatar />
      <button type="button" onClick={() => handleViewUser(record)}>
        <span>{text}</span>
      </button>
    </div>
  );
}

function renderLastDeposit(text, record, currencySymbol) {
  return (
    <div className={styles.lastDepositCol}>
      <span>
        {formatCurrency(Number(text ?? 0), currencySymbol, 2)}
      </span>
      <span className={styles.lastDepositColDate}>
        {formatTimestampOrDisplay(
          record.last_deposit_date_timestamp,
          record.last_deposit_date,
          "MMM DD, HH:mm",
        )}
      </span>
    </div>
  );
}

function renderWithdrawalAmount(text, currencySymbol) {
  return (
    <span>
      {formatCurrency(Number(text ?? 0), currencySymbol, 2)}
    </span>
  );
}

function renderFirstTimeWithdrawalIcon(isFirstTimeWithdrawal) {
  const isFtw =
    isFirstTimeWithdrawal === 1 ||
    isFirstTimeWithdrawal === "1" ||
    isFirstTimeWithdrawal === true;
  return isFtw ? (
    <CheckCircleFilled style={{ color: "#52c41a", fontSize: 18 }} />
  ) : (
    <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 18 }} />
  );
}

function renderReviewer(text) {
  return <span>{text || "-"}</span>;
}

function renderReviewTime(text) {
  return <span>{convertToUserTimezone(text)}</span>;
}

function renderRowActions(
  record,
  {
    canReviewWithdrawal,
    openApproveModal,
    openDeclinedModal,
    openRefundModal,
    openDetailsModal,
    openPaymentDetailsDrawer,
    approveTransactionLoading,
    approvingTransactionId,
    refundTransactionLoading,
    canRefundWithdrawal,
  },
) {
  return (
    <div className={styles.actionButtonGroup}>
      <button
        type="button"
        className={`${styles.actionButton} ${styles.approve} ${!canReviewWithdrawal ? styles.disabled : ""}`}
        onClick={() => openApproveModal(record)}
        disabled={
          approveTransactionLoading ||
          refundTransactionLoading ||
          !canReviewWithdrawal
        }
      >
        {approveTransactionLoading &&
        approvingTransactionId === record.transaction_id ? (
          <RebetLoader />
        ) : (
          <CheckOutlined />
        )}
      </button>
      <button
        type="button"
        className={`${styles.actionButton} ${styles.reject} ${!canReviewWithdrawal ? styles.disabled : ""}`}
        onClick={() => openDeclinedModal(record)}
        disabled={
          approveTransactionLoading ||
          refundTransactionLoading ||
          !canReviewWithdrawal
        }
      >
        <CloseOutlined />
      </button>
      {canRefundWithdrawal && (
        <button
          type="button"
          aria-label={AppStrings.WITHDRAWAL_REFUND_ACTION}
          className={`${styles.actionButton} ${styles.refund}`}
          onClick={() => openRefundModal(record)}
          disabled={
            approveTransactionLoading ||
            refundTransactionLoading ||
            !canReviewWithdrawal
          }
        >
          <RollbackOutlined />
        </button>
      )}
      <button
        type="button"
        className={`${styles.actionButton} ${styles.details} ${!canReviewWithdrawal ? styles.disabled : ""}`}
        onClick={() => openDetailsModal(record)}
        disabled={!canReviewWithdrawal}
      >
        <EyeOutlined />
      </button>
      <button
        type="button"
        className={`${styles.actionButton} ${styles.details} ${!canReviewWithdrawal ? styles.disabled : ""}`}
        aria-label={AppStrings.PAYMENT_DETAILS}
        onClick={() => openPaymentDetailsDrawer(record)}
        disabled={!canReviewWithdrawal}
      >
        <CreditCardOutlined />
      </button>
    </div>
  );
}

function buildInProgressColumns({
  handleViewUser,
  openApproveModal,
  openDeclinedModal,
  openRefundModal,
  openDetailsModal,
  openPaymentDetailsDrawer,
  canReviewWithdrawal,
  approveTransactionLoading,
  approvingTransactionId,
  refundTransactionLoading,
  canRefundWithdrawal,
  currencySymbol,
}) {
  return [
    {
      title: AppStrings.REQUEST_DATE,
      dataIndex: "request_date_timestamp",
      key: "request_date",
      width: 160,
      render: (text) => renderRequestDate(text),
    },
    {
      title: AppStrings.USERNAME,
      dataIndex: "username",
      key: "username",
      width: 150,
      render: (text, record) =>
        renderUsernameCell(text, record, handleViewUser),
    },
    {
      title: AppStrings.TRANSACTION_ID,
      dataIndex: "transaction_id",
      key: "transaction_id",
      width: 250,
    },
    {
      title: AppStrings.LAST_DEPOSIT_AMOUNT,
      dataIndex: "last_deposit_amount",
      key: "last_deposit_amount",
      width: 140,
      render: (text, record) =>
        renderLastDeposit(text, record, currencySymbol),
    },
    {
      title: AppStrings.WITHDRAWAL_AMOUNT,
      dataIndex: "withdrawal_amount",
      key: "withdrawal_amount",
      width: 160,
      render: (text) => renderWithdrawalAmount(text, currencySymbol),
    },
    {
      title: <WalletColumnTitle />,
      dataIndex: "cash_balance",
      key: "wallet",
      width: 200,
      render: (_text, record) => (
        <WalletBalanceCell
          balance={record.cash_balance}
          percentChange7d={record.cash_balance_7d_change_pct}
        />
      ),
    },
    {
      title: AppStrings.FTW,
      dataIndex: "is_first_time_withdrawal",
      key: "is_first_time_withdrawal",
      width: 70,
      align: "center",
      render: (isFirstTimeWithdrawal) =>
        renderFirstTimeWithdrawalIcon(isFirstTimeWithdrawal),
    },
    {
      title: AppStrings.PAYMENT_GATEWAY,
      dataIndex: "payment_gateway",
      key: "payment_gateway",
      width: 120,
    },
    {
      title: AppStrings.PAM_REVIEWER,
      dataIndex: "reviewed_by",
      key: "reviewed_by",
      width: 150,
      render: (text) => renderReviewer(text),
    },
    {
      title: AppStrings.REVIEW_TIME,
      dataIndex: "review_start_timestamp",
      key: "review_start_timestamp",
      width: 150,
      render: (text) => renderReviewTime(text),
    },
    {
      title: AppStrings.STATE,
      dataIndex: "subregion",
      key: "subregion",
      width: 140,
      render: (_, record) => {
        const v = record?.subregion;
        if (typeof v !== "string" || v.trim() === "") {
          return <span style={{ color: "#6B7280" }}>—</span>;
        }
        return <span>{v}</span>;
      },
    },
    {
      title: AppStrings.METHOD,
      dataIndex: "method",
      key: "method",
      width: 160,
      render: (_, record) => {
        const v = record?.method ?? record?.withdrawal_method;
        if (typeof v !== "string" || v.trim() === "") {
          return <span style={{ color: "#6B7280" }}>—</span>;
        }
        return <span>{v}</span>;
      },
    },
    {
      title: AppStrings.ACTIONS,
      key: "actions",
      width: canRefundWithdrawal ? 180 : 140,
      fixed: "right",
      render: (_, record) =>
        renderRowActions(record, {
          canReviewWithdrawal,
          openApproveModal,
          openDeclinedModal,
          openRefundModal,
          openDetailsModal,
          openPaymentDetailsDrawer,
          approveTransactionLoading,
          approvingTransactionId,
          refundTransactionLoading,
          canRefundWithdrawal,
        }),
    },
  ];
}

function InProgressTable({
  canReviewWithdrawal,
  canRefundWithdrawal,
  currencySymbol,
}) {
  const {
    inProgressTransactions,
    loadingInProgress,
    handleViewUser,
    openApproveModal,
    openDeclinedModal,
    openRefundModal,
    openDetailsModal,
    openPaymentDetailsDrawer,
    approveTransactionLoading,
    approvingTransactionId,
    refundTransactionLoading,

    inProgressPageSize,
    setInProgressPageSize,
    inProgressCurrentPage,
    setInProgressCurrentPage,
    inProgressTotalCount,
  } = useWithdrawalReview();

  // BE returns `method` as a display name (e.g. "Bank ACH"); render directly.

  const dataSource = useMemo(
    () => inProgressTransactions?.transactions ?? [],
    [inProgressTransactions],
  );

  const columns = useMemo(
    () =>
      buildInProgressColumns({
        handleViewUser,
        openApproveModal,
        openDeclinedModal,
        openRefundModal,
        openDetailsModal,
        openPaymentDetailsDrawer,
        canReviewWithdrawal,
        approveTransactionLoading,
        approvingTransactionId,
        refundTransactionLoading,
        canRefundWithdrawal,
        currencySymbol,
      }),
    [
      handleViewUser,
      openApproveModal,
      openDeclinedModal,
      openRefundModal,
      openDetailsModal,
      openPaymentDetailsDrawer,
      canReviewWithdrawal,
      approveTransactionLoading,
      approvingTransactionId,
      refundTransactionLoading,
      canRefundWithdrawal,
      currencySymbol,
    ],
  );

  return (
    <>
      <DataTable
        loading={loadingInProgress}
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        scrollPreset="xWide"
        rowKey={(record) => record.transaction_id}
        className={styles.customTable}
      />
      <CustomPagination
        total={inProgressTotalCount || 0}
        pageSize={inProgressPageSize}
        currentPage={inProgressCurrentPage}
        onPageSizeChange={(value) => {
          setInProgressCurrentPage(1);
          setInProgressPageSize(value);
        }}
        onPageChange={(page) => setInProgressCurrentPage(page)}
      />
    </>
  );
}

export default InProgressTable;
